/**
 * @file as-app.ts
 *
 * Root application component. Most of the behaviour is in the mixins. What is
 * left here is app identity, env, auth, versions, the shadow DOM and the
 * asGlobals debug hooks.
 *
 * Button names are handled by whoever owns the concern, not here. A panel wires
 * its own buttons (components/modals/modalPanel.ts). `header-help-button` is
 * deliberately handled nowhere: the header draws it as a real anchor, which
 * navigates even in webviews that block scripted window.open.
 *
 * Mixin chain, inner to outer. composeMixins applies them left to right, so the
 * first sits closest to HTMLElement:
 *   LocalizationMixin, InitQueueMixin, ModalMixin, ErrorMixin, ProjectMixin,
 *   HistoryMixin, TransferMixin, FileDropMixin, BusyMixin, ExitMixin,
 *   GrabCADMixin, SysDataMixin, AsElMixin, AsApp
 *
 * Plain modules the mixins build on are in lib/: appConfig.ts for app info and
 * app-list queries, saveWithFilePicker.ts for the File System Access save path
 * (not wired in). ModalMixin's panels are built by modals/modalTemplates.ts.
 */

// ============================================================================
// Component side-effect imports
// ============================================================================
import '../modals/as-modal-overlay.ts';
import '../error/as-error-info.ts';
import '../overlays/as-notifications.ts';
import '../modals/as-modal-unsaved-warning.ts';
import '../modals/as-modal-save-project.ts';
import '../modals/as-modal-restore-session.ts';
import '../modals/as-modal-expired-session.ts';
import '../modals/as-modal-accept-terms.ts';
import '../modals/as-modal-paramate-error.ts';
import '../../features/importExport/lib/exportProgressPlugin.ts';

// ============================================================================
// Store / data
// ============================================================================
import { internalData } from '../../store/store.ts';
import { addAsGlobals } from '../../asGlobals.ts';
import { _registerSingleton, _unregisterSingleton, asConfigurator, whenConfigurator } from '../../util/singletons.ts';
import { envReady, markEnvReady } from '../../data/envRegistry.ts';
import { resolveSessionEnv } from '../../features/host/host.ts';
import { deleteAllProjectsForApp } from '../../features/db/db.ts';
import packageJson from '../../../package.json';

// ============================================================================
// API / utilities
// ============================================================================
import { getUserData, type UserData } from '../../features/serverApi/asApi.ts';
import type { AsConfigurator } from '../paramate/as-configurator.ts';
import { fullAppSuiteRootURL, isProduction, localDev, inIframe, noAPI, isOnPrem, deployment, urlLang, storedLang } from '../../data/constants.ts';
import { detectDeviceInfo, devlog, UAContainsMobileKeywords } from '../../util/utils.ts';
import { params } from '../../store/paramStore.ts';
import { sendEv } from '../../data/events.ts';
import styleText from './as-app.css?raw';
import modalStyleText from '../modals/modalTemplates.css?raw';

// ============================================================================
// Sibling modules
// ============================================================================
import { appInfo, appList, filterCategories, getAppInfo, getAppFolderName } from './lib/appConfig.ts';

// ============================================================================
// Mixins
// ============================================================================
import { composeMixins } from './mixins/mixin-types-utils.ts';
import { LocalizationMixin, type LocalizationMixinMembers } from './mixins/localization-mixin.ts';
import { InitQueueMixin, type InitQueueMixinMembers } from './mixins/init-queue-mixin.ts';
import { ModalMixin, type ModalMixinMembers } from './mixins/modal-mixin.ts';
import { ErrorMixin, type ErrorMixinMembers } from './mixins/error-mixin.ts';
import { ProjectMixin, type ProjectMixinMembers, type ProjectMixinInternals } from './mixins/project-mixin.ts';
import { HistoryMixin, type HistoryMixinMembers } from './mixins/history-mixin.ts';
import { TransferMixin, type TransferMixinMembers, type TransferMixinInternals } from './mixins/transfer-mixin.ts';
import { FileDropMixin, type FileDropMixinMembers } from './mixins/file-drop-mixin.ts';
import { BusyMixin, type BusyMixinMembers } from './mixins/busy-mixin.ts';
import { ExitMixin, type ExitMixinMembers } from './mixins/exit-mixin.ts';
import { GrabCADMixin, type GrabCADMixinMembers } from './mixins/grabcad-mixin.ts';
import { SysDataMixin, type SysDataMixinMembers } from './mixins/sysdata-mixin.ts';
import { AsElMixin, type AsElMixinMembers } from '../base.ts';

devlog('[CONSTANTS] LocalDev', localDev, 'Production?', isProduction);

// ============================================================================
// Type definitions
// ============================================================================

declare global {
  interface Window {
    ParamateConfigurator: any;
    ParamateViewportWebGL: any;
  }
}

export interface GetMeshEvent extends CustomEvent<{
  resolve: Function;
  reject: Function;
  finalized?: boolean;
  filename?: string;
  type?: string;
}> {}

// ============================================================================
// Mixin composition
// ============================================================================

const AsAppBase = composeMixins(
  HTMLElement,
  LocalizationMixin,
  InitQueueMixin,
  ModalMixin,
  ErrorMixin,
  ProjectMixin,
  HistoryMixin,
  TransferMixin,
  FileDropMixin,
  BusyMixin,
  ExitMixin,
  GrabCADMixin,
  SysDataMixin,
  AsElMixin,
);

// ============================================================================
// AsApp component
// ============================================================================

export interface AsApp extends
  LocalizationMixinMembers,
  InitQueueMixinMembers,
  ModalMixinMembers,
  ErrorMixinMembers,
  HistoryMixinMembers,
  ProjectMixinMembers,
  ProjectMixinInternals,
  TransferMixinMembers,
  TransferMixinInternals,
  FileDropMixinMembers,
  BusyMixinMembers,
  ExitMixinMembers,
  GrabCADMixinMembers,
  SysDataMixinMembers,
  AsElMixinMembers {}

export class AsApp extends AsAppBase {
  static readonly tag = 'as-app' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private static readonly _modalSheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(modalStyleText);
    return sheet;
  })();

  /** The Paramate param table, the same object as the `params` export. It sits
   *  here because it is where apps look for it, like `appData` and `sysData`.
   *  Read-only: writes go through `<as-configurator>.setParamValue()`. */
  get params(): typeof params {
    return params;
  }

  public appId: string;
  public mobileDetected: boolean | undefined = undefined;
  /** The signed-in user, from the bootstrap fetch and every keepalive refetch
   *  after it. Null when signed out or in a context that never asks. */
  public userData: UserData | null = null;
  /** The component library version, from package.json at build time. */
  readonly version: string = packageJson.version;
  private _trcadVersions: { trCad: string; script: string } | undefined;

  /**
   * Answers `as-get-app` with an `as-app` event aimed at the requester, so an
   * element can find the app without waiting for the startup broadcast.
   * On `window` and registered in the constructor, so it is live before any
   * child connects and elements portaled to document.body can still reach it.
   */
  private _handleGetApp = (event: Event): void => {
    const { requester } = (event as CustomEvent<{ requester: EventTarget }>).detail;
    sendEv(requester, 'as-app', { bubbles: false, detail: { sender: this } });
  };

  /** Whether this session gates first paint on the auth check. Fixed in the
   *  constructor and never cleared: `internalData.authGateUp` is the flag that
   *  goes down when the check resolves. */
  private readonly _authGated: boolean;

  get appInfo() { return appInfo; }
  get appList() { return appList; }
  get filterCategories() { return filterCategories; }
  get isProduction() { return isProduction; }

  /** Resolves once the env is settled (envRegistry's envReady). Await it before
   *  reading `internalData.appEnv`: the value is null until the userData fetch
   *  lands, which is after most app startup code runs. */
  get envReady(): Promise<void> { return envReady; }

  /**
   * Returns a promise that resolves with the app info once it is loaded.
   */
  public async getAppInfo(): Promise<any> {
    return getAppInfo();
  }

  static get observedAttributes(): string[] {
    return [...super.observedAttributes, 'onprem', 'env', 'lang', 'app-id', 'server-root', 'assets-path', 'debug-log-notifications', 'use-local-assets', 'file-droppable', 'disable-undo-history'];
  }

  // ==========================================================================
  // Versions
  // ==========================================================================

  getVersion(): string { return this.version; }

  /** The TrCAD and script versions from the configurator's `main:metadata`
   *  params. Fetched once and cached; undefined fields when there is no
   *  configurator or the params don't carry them. */
  async getTrCADVersions(): Promise<{ trCad: string | undefined; script: string | undefined }> {
    if (this._trcadVersions) return this._trcadVersions;
    const output: { trCad: string | undefined; script: string | undefined } = { trCad: undefined, script: undefined };
    if (!asConfigurator()) { console.warn('[VERSIONING] as-configurator element not found'); return output; }
    try {
      await whenConfigurator();
      output.trCad = params.get('main:metadata:trcadVersion')?.default;
      output.script = params.get('main:metadata:scriptVersion')?.default;
      if (!output.trCad) devlog('[VERSIONING] trcadVersion not found in params main:metadata');
      if (!output.script) devlog('[VERSIONING] scriptVersion not found in params main:metadata');
      devlog('[VERSIONING]', output);
      this._trcadVersions = output as { trCad: string; script: string };
    } catch (err) {
      console.error('[VERSIONING] Error getting TrCAD version:', err);
    }
    return output;
  }

  /** Every version in one object: the component library, the app (from the
   *  `app-version` attribute), TrCAD and the TrCAD script. */
  async getVersions(): Promise<{ components: string; app: string | null; trCad: string | undefined; trCadScript: string | undefined }> {
    const { trCad, script } = await this.getTrCADVersions();
    return { components: this.version, app: this.getAttribute('app-version'), trCad, trCadScript: script };
  }

  // ==========================================================================
  // Init
  // ==========================================================================

  constructor() {
    super();
    window.addEventListener('as-get-app', this._handleGetApp);
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsApp._sheet,
      AsApp._modalSheet,
    ];
    this.appId = this.getAttribute('app-id') ?? 'invalid';

    // Environment Handling
    this._initEnvironment();
    this._initLang();

    const serverRoot = this.getAttribute('server-root');
    const assetsPath = this.getAttribute('assets-path');
    if (serverRoot) internalData.serverRoot = serverRoot;
    if (assetsPath) internalData.assetsPath = assetsPath;
    if (this.hasAttribute('use-local-assets')) internalData.useLocalAssets = true;

    // Hide the (already-parsed, light-DOM) app content until the auth check
    // in connectedCallback resolves, so an unauthenticated visitor never sees
    // a flash of the full UI before being redirected to sign in.
    this._authGated = !localDev && !inIframe && !noAPI && !isOnPrem && !this.hasAttribute('dev-mode');
    this._setAuthGate(this._authGated);

    this.shadowRoot && (
      this.shadowRoot.innerHTML = /*html*/`
        <div id="as-app-container">
          <slot></slot>
        </div>
        <div id="auth-gate"><as-spinner></as-spinner></div>
        <as-notifications default-timeout="5000"></as-notifications>
        <as-modal-overlay id="as-app-overlay" mode="auto" visible-child="unsaved-warning">
          <as-error-info id="main-error-panel"></as-error-info>
          <as-mobile-warning id="mobile-warning"></as-mobile-warning>
          <as-modal-unsaved-warning id="unsaved-warning" disable-bg-close></as-modal-unsaved-warning>
          <as-modal-save-project id="save-project"></as-modal-save-project>
          <as-modal-expired-session id="expired-session" disable-bg-close></as-modal-expired-session>
          <as-modal-accept-terms id="accept-terms" disable-bg-close></as-modal-accept-terms>
        </as-modal-overlay>
      `
    );
  }

  // ==========================================================================
  // Connected / disconnected
  // ==========================================================================

  /** The app server has its own session timeout, so ride the configurator's
   *  13-minute keepalive ('as-heartbeat') to keep it warm. Also refreshes
   *  userData, and getUserData() fires 'as-auth-update' off the back of it. */
  private _onHeartbeat = (): void => {
    getUserData()
      .then(res => { this.userData = res.userData; })
      .catch(err => devlog('getUserData keepalive failed:', err));
  };

  async connectedCallback() {
    // Claim the 'app' refs slot: backs asApp()/requireAsApp() and resolves
    // any pending whenAsApp().
    _registerSingleton('app', this);
    await super.connectedCallback?.();
    
    this._handleGlobalDebugFlags();

    window.addEventListener('as-heartbeat', this._onHeartbeat);

    this._initAuth();

    // Env detection from userData (envRegistry.ts), kept apart from the auth
    // branches above because those skip the fetch in an iframe and this still has
    // to resolve there. getUserData() dedupes, so it costs no extra request.
    // Always settles: with no userData, resolveSessionEnv asks each host's
    // checkHost, then falls back to 'default'.
    // Anything awaiting envReady would hang otherwise.
    if (!this.hasAttribute('env')) {
      getUserData()
        .then(res => resolveSessionEnv(res.userData))
        .catch(() => resolveSessionEnv(null))
        .then(env => {
          this.setAttribute('env', env);
          markEnvReady();
          sendEv(window, 'as-intrn-env-ready');
        });
    }

    if (this.appId === 'invalid' || this.appId === 'template') {
      alert('app-id is required attribute on as-app');
    } else {
      internalData.appId = this.appId;
      // Download gating: the appList reports per-app downloadEnabled
      // (license-gated). Missing entries or an unavailable list leave
      // canDownload at its default (true).
      getAppInfo().then(info => {
        internalData.canDownload = info?.[this.appId]?.downloadEnabled !== false;
      }).catch(() => {});
    }

    this.sendLog(this.appId === 'storefront' ? 'storefront_opened' : 'app_opened', { browser: navigator.userAgent });

    sendEv(window, 'as-app', { detail: { sender: this } });

    const deviceInfo = detectDeviceInfo();
    if (deviceInfo.hasTouchScreen) {
      if (this.hasAttribute('mobile-view')) {
        console.warn('Custom mobile view is not implemented yet.');
      }
      devlog('[MOBILE]', UAContainsMobileKeywords());
      if (UAContainsMobileKeywords()) this.mobileDetected = true;
      if (UAContainsMobileKeywords() && this.appId !== 'storefront') {
        sendEv(this.modalOverlay!, 'as-set-visible-child', { detail: { id: 'mobile-warning' } });
        sendEv(this.modalOverlay!, 'as-open-modal');
      }
    }
  }

  disconnectedCallback(): void {
    // Release the 'app' refs slot, but only if we are still the one in it.
    _unregisterSingleton('app', this);
    window.removeEventListener('as-get-app', this._handleGetApp);
    window.removeEventListener('as-heartbeat', this._onHeartbeat);
    super.disconnectedCallback?.();
  }

  /**
   * Fetch the signed-in user, and when first paint is gated behind that check
   * either reveal the app or send an unauthenticated visitor to sign-in.
   * `window.stopRedir = true` on the page holds the redirect back, for the test
   * pages in html/.
   */
  private async _initAuth(): Promise<void> {
    // An ungated iframe or noAPI page never asks. Everything else wants the user
    // data, gated or not (dummy data on localDev, noAPI and onprem, see
    // _getUserDataImpl).
    const wantsUserData = this._authGated || isOnPrem || localDev
      || (!inIframe && !noAPI);
    if (!wantsUserData) return;

    const res = await getUserData();
    this.userData = res.userData;
    // Nothing gated means no redirect and no gate to lower.
    if (!this._authGated) return;

    if (window.stopRedir) {
      devlog('User data response:', res.userData);
    } else if (!res.isAuthenticated && !res.transportFailed) {
      // Redirect by deployment folder name, not app id: sign-in lands the user
      // on /apps/{redirApp}, and the id can drift from the folder name (see
      // getAppFolderName). Leave the gate up, since we are navigating away.
      const redirApp = await getAppFolderName(this.appId);
      window.location.href = `${fullAppSuiteRootURL}/?modal=sign-in-form&redirApp=${encodeURIComponent(redirApp)}`;
      return;
    }

    // transportFailed lands here too: the check never got a real answer, so
    // show the app unauthenticated rather than bouncing over a server hiccup.
    this._setAuthGate(false);
  }

  /** The gate itself lives in `internalData.authGateUp`, so anything can read
   *  or subscribe to it. The attribute is a reflection of it, for the CSS in
   *  as-app.css. */
  private _setAuthGate(up: boolean): void {
    internalData.authGateUp = up;
    this.toggleAttribute('auth-checking', up);
  }

  // ==========================================================================
  // Attributes
  // ==========================================================================

  // `file-droppable` and `disable-undo-history` are handled by FileDropMixin
  // and HistoryMixin in their own attributeChangedCallback, reached through
  // the super call. They only need listing in observedAttributes above.
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    super.attributeChangedCallback?.(name, oldValue, newValue);
    switch (name) {
      case 'lang':         this._loc?.updateLang(newValue ?? 'en'); break;
      case 'app-id':       this.appId = newValue ?? 'invalid'; break;
      case 'server-root':  if (newValue) internalData.serverRoot = newValue; break;
      case 'assets-path':  if (newValue) internalData.assetsPath = newValue; break;
      case 'env':
        internalData.appEnv = newValue ?? null;
        break;
    }
  }

  // ==========================================================================
  // Environment and language
  // ==========================================================================

  private _initEnvironment(): void {
    // Evict any stale cached env from the old 24h-localStorage scheme.
    localStorage.removeItem('as-app-env');

    // The deployment is fixed at load (see constants.ts). It only needs
    // publishing for sysData. [DEPRECATED] The onprem attribute goes in 0.19;
    // read sysData.deployment instead.
    internalData.deployment = deployment;
    if (isOnPrem) this.setAttribute('onprem', '');

    const urlParams = new URLSearchParams(window.location.search);
    let env = urlParams.get('env') ?? this.getAttribute('env');
    // [DEPRECATED] env="onprem" means deployment="onprem" (constants.ts warns),
    // and leaves the env to detection.
    if (env === 'onprem') { env = null; this.removeAttribute('env'); }

    if (env) {
      this.setAttribute('env', env);
      // An explicit env is the final answer, so it is ready now. The userData
      // detection in connectedCallback skips itself when env is already set.
      markEnvReady();
      sendEv(window, 'as-intrn-env-ready');
    }
    internalData.appEnv = env ?? null;
  }

  // A `?lang=` URL param forces the language, for a host that manages language
  // itself (GrabCAD Print appends its own on launch). Not gated on env, so any
  // host can use it. Otherwise fall back to the last language the user picked,
  // stored by LocalizationController, but only if the markup set no `lang`
  // attribute: an explicit attribute is the author's call.
  private _initLang(): void {
    const lang = urlLang ?? (this.hasAttribute('lang') ? null : storedLang);
    if (!lang) return;
    // Drive the controller rather than setting the attribute: a setAttribute
    // from inside a constructor may not run its callback synchronously, so boot
    // code reading _loc.language could see the default. updateLang() reflects
    // the attribute itself, synchronously either way.
    this._loc.updateLang(lang);
  }

  // ==========================================================================
  // Debug hooks
  // ==========================================================================

  /** Console hooks for testing session expiry by hand. See
   *  docs/sessionsAndExpiry.md for how they are meant to be used. */
  private _handleGlobalDebugFlags(): void {
    addAsGlobals({
      // Run the expired-session flow without anything actually expiring.
      triggerExpiry: () => {
        if (asConfigurator()?.initialized) this._handleSessionExpired();
      },

      // Point the client at a session id the server has never heard of, so the
      // next configurator action fails.
      invalidateSession: () => {
        const cfg = asConfigurator()?.configurator;
        if (!cfg) return;
        cfg.configSession = 'invalid-session-id';
        console.log('Session invalidated. The next configurator action will fail.');
      },

      /**
       * The machine sleeping past the session TTL with the tab visible: pause the
       * keepalive so the server session really expires, then fire the overdue beat
       * after `minutes`, as the browser does on resume. The 404 must route into
       * the expired-session flow, not the global error modal.
       *
       * simulateSleep() waits 32 min for a real expiry. simulateSleep(0.1), then
       * invalidateSession() within ~6s, gets there instantly.
       */
      simulateSleep: (minutes = 32) => {
        const configuratorEl = asConfigurator();
        if (!configuratorEl?.initialized) {
          console.warn('[SIMULATE-SLEEP] Configurator not initialized');
          return;
        }
        configuratorEl.pauseSessionHeartbeat();
        console.log(`[SIMULATE-SLEEP] Heartbeat paused; overdue keepalive fires in ${minutes} min`);
        setTimeout(() => {
          console.log('[SIMULATE-SLEEP] Firing overdue keepalive against the expired session');
          configuratorEl.triggerHeartbeat();
        }, minutes * 60 * 1000);
      },

      /** Expire the session for real, instantly: DELETE it server-side with the
       *  credentials the SDK already holds, then heartbeat. The 404 that comes
       *  back is the one a real expiry produces, with no waiting. */
      forceSessionExpire: async () => {
        const configuratorEl = asConfigurator();
        const cfg = configuratorEl?.configurator;
        if (!configuratorEl?.initialized || !cfg) {
          console.warn('[FORCE-EXPIRE] Configurator not initialized');
          return;
        }
        configuratorEl.pauseSessionHeartbeat();
        try {
          const res = await fetch(`${cfg.APIHub}/configSessions/${cfg.configSession}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${cfg.token}`,
              'X-API-KEY': cfg.APIKey,
            },
          });
          console.log(`[FORCE-EXPIRE] DELETE /configSessions/${cfg.configSession} -> ${res.status}`);
        } catch (e) {
          console.error('[FORCE-EXPIRE] Delete request failed', e);
          return;
        }
        console.log('[FORCE-EXPIRE] Firing heartbeat against the deleted session');
        configuratorEl.triggerHeartbeat();
      },
    });
  }

  async clearProjectsDB() {
    const count = await deleteAllProjectsForApp(this.appId);
    console.log(`Cleared ${count} projects for app ${this.appId}`);
    return count;
  }

}

AsApp.define();

// Lets wcQry('as-app') and plain document.querySelector('as-app') resolve as
// AsApp, the same as passing the class.
declare global {
  interface HTMLElementTagNameMap {
    'as-app': AsApp;
  }
}
